import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { startOfMonth, endOfMonth } from "date-fns";
import { DateField, fmtISO } from "./_shared";
import DrillDownModal from "@/components/DrillDownModal";

export function ComparativePnlReport({ currency }) {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/pnl-comparative", {
        params: { start: fmtISO(start), end: fmtISO(end) },
      });
      setData(data);
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const renderVariance = (v, pct, inverse = false) => {
    if (v == null) return "—";
    const isGood = inverse ? v < 0 : v > 0;
    const cls = Math.abs(v) < 0.005 ? "text-[var(--muted-text)]" : isGood ? "text-[#2F6B4A]" : "text-[#B4283A]";
    return (
      <span className={cls}>
        {v > 0 ? "+" : ""}{formatCurrency(v, data?.currency || "USD")}{pct != null ? ` (${pct}%)` : ""}
      </span>
    );
  };

  return (
    <div data-testid="cmp-pnl-report" className="space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <DateField label="From" value={start} onChange={setStart} testId="cmp-start" />
        <DateField label="To" value={end} onChange={setEnd} testId="cmp-end" />
      </div>
      {loading && !data && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}
      {data && (
        <>
          <div className="rounded-md border border-[var(--divider)] bg-[#FFF9F0] px-3 py-2 text-xs text-[#7A6C4E]" data-testid="cmp-currency-note">
            All figures below are a <strong>native sum in {data.currency || "USD"}</strong>. This comparative view does not apply live FX conversion.
          </div>
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--divider)] text-xs uppercase tracking-[0.2em] text-[var(--muted-text)]">
            Current: {data.current.start} → {data.current.end} · Prior: {data.prior.start} → {data.prior.end}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-wider text-[var(--muted-text)]">
              <tr>
                <th className="text-left px-5 py-2">Line</th>
                <th className="text-right px-4 py-2">Current</th>
                <th className="text-right px-4 py-2">Prior</th>
                <th className="text-right px-4 py-2">Variance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-[#EAF4EE]/40"><td colSpan={4} className="px-5 py-2 text-[10px] uppercase tracking-widest text-[#2F6B4A] font-semibold">Income</td></tr>
              {data.income.rows.map((r) => (
                <tr key={"i-" + r.category} data-testid={`cmp-income-${r.category}`}
                    className="border-b border-[var(--divider)] cursor-pointer hover:bg-[var(--hover)]"
                    title="Click to drill into current-period income"
                    onClick={() => setDrill({
                      title: `${r.category} · Current period income`,
                      params: { kind: "income", category: r.category,
                                start: data.current.start, end: data.current.end },
                    })}>
                  <td className="px-5 py-2">{r.category}<span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span></td>
                  <td className="px-4 py-2 text-right font-mono">{formatCurrency(r.current, data?.currency || "USD")}</td>
                  <td className="px-4 py-2 text-right font-mono text-[var(--muted-text)]">{formatCurrency(r.prior, data?.currency || "USD")}</td>
                  <td className="px-4 py-2 text-right font-mono">{renderVariance(r.variance, r.variance_pct)}</td>
                </tr>
              ))}
              <tr className="border-t border-[#2F4F4F] font-semibold">
                <td className="px-5 py-2">Total income</td>
                <td className="px-4 py-2 text-right font-mono" data-testid="cmp-income-total">{formatCurrency(data.income.current_total, data?.currency || "USD")}</td>
                <td className="px-4 py-2 text-right font-mono">{formatCurrency(data.income.prior_total, data?.currency || "USD")}</td>
                <td className="px-4 py-2 text-right font-mono">{renderVariance(data.income.variance, data.income.variance_pct)}</td>
              </tr>
              <tr className="bg-[#FCE7E9]/40"><td colSpan={4} className="px-5 py-2 text-[10px] uppercase tracking-widest text-[#B4283A] font-semibold">Expenses</td></tr>
              {data.expenses.rows.map((r) => (
                <tr key={"e-" + r.category} data-testid={`cmp-expense-${r.category}`}
                    className="border-b border-[var(--divider)] cursor-pointer hover:bg-[var(--hover)]"
                    title="Click to drill into current-period expenses"
                    onClick={() => setDrill({
                      title: `${r.category} · Current period expenses`,
                      params: { kind: "expense", category: r.category,
                                start: data.current.start, end: data.current.end },
                    })}>
                  <td className="px-5 py-2">{r.category}<span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span></td>
                  <td className="px-4 py-2 text-right font-mono">{formatCurrency(r.current, data?.currency || "USD")}</td>
                  <td className="px-4 py-2 text-right font-mono text-[var(--muted-text)]">{formatCurrency(r.prior, data?.currency || "USD")}</td>
                  <td className="px-4 py-2 text-right font-mono">{renderVariance(r.variance, r.variance_pct, true)}</td>
                </tr>
              ))}
              <tr className="border-t border-[#2F4F4F] font-semibold">
                <td className="px-5 py-2">Total expenses</td>
                <td className="px-4 py-2 text-right font-mono" data-testid="cmp-expense-total">{formatCurrency(data.expenses.current_total, data?.currency || "USD")}</td>
                <td className="px-4 py-2 text-right font-mono">{formatCurrency(data.expenses.prior_total, data?.currency || "USD")}</td>
                <td className="px-4 py-2 text-right font-mono">{renderVariance(data.expenses.variance, data.expenses.variance_pct, true)}</td>
              </tr>
              <tr className="border-t-2 border-[#2F4F4F] font-semibold bg-[var(--hover)]">
                <td className="px-5 py-3">Net income</td>
                <td className="px-4 py-3 text-right font-mono" data-testid="cmp-net-current">{formatCurrency(data.net_income.current, data?.currency || "USD")}</td>
                <td className="px-4 py-3 text-right font-mono">{formatCurrency(data.net_income.prior, data?.currency || "USD")}</td>
                <td className="px-4 py-3 text-right font-mono">{renderVariance(data.net_income.variance, data.net_income.variance_pct)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={data?.currency || currency}
      />
    </div>
  );
}
